import { useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Capacitor } from "@capacitor/core";
import { X, Zap, Shield, Check, Loader2, Star, Plus, Minus, Globe } from "lucide-react";
import { useIsMobile } from "../hooks/useIsMobile";
import api from "../utils/api";
import toast from "react-hot-toast";

// Apple Guideline 3.1.1 — no non-Apple payment processor may be used to unlock
// functionality from within an iOS app. Billing must happen on the website.
const IS_IOS_APP = Capacitor.getPlatform() === "ios";

// Fixed plans — base amounts excluding 18% GST
const PLANS = [
  { id: "plan_50",  calls: 50,  amount: 300, label: "Starter",  color: "#4F46E5" },
  { id: "plan_100", calls: 100, amount: 550, label: "Standard", color: "#0369A1" },
  { id: "plan_150", calls: 150, amount: 750, label: "Growth",   color: "#7C3AED" },
  { id: "plan_200", calls: 200, amount: 900, label: "Pro",      color: "#059669", best: true },
];

const TOPUP_RATE  = 3.75;
const GST_RATE    = 0.18;
const TOPUP_PICKS = [100, 200, 500, 1000];
const TOPUP_STEP  = 100;
const TOPUP_MIN   = 100;
const TOPUP_MAX   = 5000;
const FLEET_UPGRADE_AMOUNT = 2999; // matches backend/src/routes/payment.js — backend re-validates regardless

function gstTotal(base) { return Math.round(base * (1 + GST_RATE)); }

export default function PaymentModal({ onClose, mode = "plans" }) {
  const isMobile = useIsMobile();

  const [paying,       setPaying]       = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(PLANS[1]);
  const [topupCalls,   setTopupCalls]   = useState(100);

  const isTopup        = mode === "topup";
  const isFleetUpgrade = mode === "fleet_upgrade";
  const amount   = isFleetUpgrade ? FLEET_UPGRADE_AMOUNT : isTopup ? Math.round(topupCalls * TOPUP_RATE * 100) / 100 : selectedPlan.amount;
  const apiCalls = isFleetUpgrade ? 0 : isTopup ? topupCalls : selectedPlan.calls;
  const rateType = isFleetUpgrade ? "fleet_upgrade" : isTopup ? "topup" : selectedPlan.id;
  const grandTotal = gstTotal(amount);

  function adjustTopup(delta) {
    setTopupCalls(prev => {
      const next = prev + delta;
      return Math.max(TOPUP_MIN, Math.min(TOPUP_MAX, Math.round(next / TOPUP_STEP) * TOPUP_STEP));
    });
  }

  async function handlePay() {
    setPaying(true);
    try {
      const { data } = await api.post("/api/payment/phonepe/initiate", { amount, apiCalls, rateType });
      // Redirect the browser to PhonePe's hosted checkout page
      window.location.href = data.checkoutUrl;
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not initiate payment. Please try again.");
      setPaying(false);
    }
  }

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      style={{
        position: "fixed", inset: 0, zIndex: 99999,
        background: "rgba(17,24,39,0.52)",
        display: "flex",
        alignItems: isMobile ? "flex-end" : "center",
        justifyContent: "center",
        padding: isMobile ? 0 : 16,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <motion.div
        initial={{ scale: isMobile ? 1 : 0.96, y: isMobile ? 80 : 16, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{   scale: isMobile ? 1 : 0.96, y: isMobile ? 80 : 16, opacity: 0 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
        style={{
          width: "100%",
          maxWidth: isMobile ? "100%" : ((isTopup || isFleetUpgrade) ? 420 : 520),
          background: "#ffffff",
          border: isMobile ? "none" : "1px solid #E5E7EB",
          borderRadius: isMobile ? "20px 20px 0 0" : 16,
          overflow: "hidden",
          boxShadow: isMobile ? "0 -8px 40px rgba(0,0,0,0.2)" : "0 24px 64px rgba(0,0,0,0.14)",
          maxHeight: isMobile ? "92vh" : "none",
          overflowY: isMobile ? "auto" : "visible",
        }}
      >
        {isMobile && (
          <div style={{ padding: "12px 0 4px", display: "flex", justifyContent: "center" }}>
            <div style={{ width: 40, height: 4, borderRadius: 99, background: "#E2E8F0" }} />
          </div>
        )}

        {/* Accent bar */}
        <div style={{ height: 4, background: isFleetUpgrade ? "#059669" : isTopup ? "#7C3AED" : "#4F46E5" }} />

        {/* Header */}
        <div style={{
          padding: "18px 22px 14px",
          borderBottom: "1px solid #F3F4F6",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111827" }}>
              {isFleetUpgrade ? "Upgrade to Fleet Management" : isTopup ? "Top Up API Calls" : "Choose Your Plan"}
            </div>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 3 }}>
              {isFleetUpgrade
                ? "One-time purchase · unlocks instantly · no expiry"
                : isTopup
                ? `₹${TOPUP_RATE}/call + 18% GST · in multiples of ${TOPUP_STEP} calls`
                : "One-time purchase · instant activation · no expiry"}
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: "none", border: "1px solid #E5E7EB", borderRadius: 8,
                width: 32, height: 32, cursor: "pointer", color: "#9CA3AF",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}
            ><X size={14} /></button>
          )}
        </div>

        <div style={{ padding: "18px 22px 22px" }}>

          {/* ── FLEET UPGRADE ── */}
          {isFleetUpgrade && (
            <div style={{
              background: "#F0FDF4", border: "1.5px solid #BBF7D0",
              borderRadius: 12, padding: "16px 18px", marginBottom: 18,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#059669", marginBottom: 10 }}>
                What you unlock
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {[
                  "Vehicle documents — RC, insurance, permit, PUC with expiry alerts",
                  "Maintenance scheduling",
                  "Fuel logs with automatic mileage tracking",
                  "Expense tracking — tolls, repairs, insurance, salaries",
                  "Driver performance scorecards",
                  "Auto-generated monthly fleet reports",
                  "The Fleet Dashboard — everything in one view",
                ].map(text => (
                  <div key={text} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <Check size={13} color="#059669" style={{ marginTop: 2, flexShrink: 0 }} />
                    <span style={{ fontSize: 12.5, color: "#374151" }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── PLANS ── */}
          {!isTopup && !isFleetUpgrade && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 18 }}>
                {PLANS.map(plan => {
                  const sel = selectedPlan.id === plan.id;
                  return (
                    <button
                      key={plan.id}
                      onClick={() => setSelectedPlan(plan)}
                      style={{
                        position: "relative",
                        padding: "14px 14px 12px",
                        borderRadius: 12,
                        border: `2px solid ${sel ? plan.color : "#E5E7EB"}`,
                        background: sel ? `${plan.color}0A` : "#FAFAFA",
                        cursor: "pointer",
                        textAlign: "left",
                        transition: "all 0.14s",
                      }}
                    >
                      {plan.best && (
                        <span style={{
                          position: "absolute", top: -1, right: 10,
                          fontSize: 9, fontWeight: 800, color: "#fff",
                          background: "#059669", borderRadius: "0 0 6px 6px",
                          padding: "2px 8px", letterSpacing: 0.5,
                          display: "flex", alignItems: "center", gap: 3,
                        }}>
                          <Star size={8} fill="#fff" /> BEST
                        </span>
                      )}
                      <div style={{ fontSize: 11, fontWeight: 700, color: sel ? plan.color : "#9CA3AF", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        {plan.label}
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: sel ? plan.color : "#111827", lineHeight: 1 }}>
                        {plan.calls}
                        <span style={{ fontSize: 12, fontWeight: 500, color: "#6B7280" }}> calls</span>
                      </div>
                      <div style={{ marginTop: 6 }}>
                        <span style={{ fontSize: 16, fontWeight: 700, color: sel ? plan.color : "#374151" }}>
                          ₹{gstTotal(plan.amount).toLocaleString("en-IN")}
                        </span>
                        <span style={{ fontSize: 10, color: "#9CA3AF", marginLeft: 4 }}>incl. GST</span>
                      </div>
                      <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2 }}>₹{plan.amount} + 18% GST</div>
                      {sel && (
                        <div style={{
                          position: "absolute", top: 10, right: 10,
                          width: 18, height: 18, borderRadius: "50%",
                          background: plan.color, display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <Check size={10} color="#fff" strokeWidth={3} />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              <div style={{
                background: "#F9FAFB", border: "1px solid #F3F4F6",
                borderRadius: 10, padding: "10px 14px", marginBottom: 18,
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {[
                    { icon: Zap,    text: `${selectedPlan.calls} live FASTag API calls` },
                    { icon: Shield, text: "Real-time vehicle tracking" },
                    { icon: Check,  text: "Toll history & route visualisation" },
                  ].map(({ icon: Icon, text }) => (
                    <div key={text} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Icon size={12} color={selectedPlan.color} />
                      <span style={{ fontSize: 12, color: "#374151" }}>{text}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #E5E7EB", fontSize: 11, color: "#059669", fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
                  <Star size={10} fill="#059669" /> Any plan unlocks top-up at ₹{TOPUP_RATE}/call afterwards
                </div>
              </div>
            </>
          )}

          {/* ── TOPUP ── */}
          {isTopup && (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                  Quick picks
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {TOPUP_PICKS.map(calls => {
                    const sel = topupCalls === calls;
                    return (
                      <button
                        key={calls}
                        onClick={() => setTopupCalls(calls)}
                        style={{
                          padding: "10px 12px", borderRadius: 8, cursor: "pointer",
                          background: sel ? "#F5F3FF" : "#F9FAFB",
                          border: `1.5px solid ${sel ? "#7C3AED" : "#E5E7EB"}`,
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          transition: "all 0.12s",
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 600, color: sel ? "#7C3AED" : "#111827" }}>
                          {calls.toLocaleString("en-IN")} calls
                        </span>
                        <span style={{ fontSize: 12, color: sel ? "#7C3AED" : "#6B7280", fontWeight: 500 }}>
                          ₹{gstTotal(Math.round(calls * TOPUP_RATE * 100) / 100).toLocaleString("en-IN")}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                  Custom amount
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button
                    onClick={() => adjustTopup(-TOPUP_STEP)}
                    style={{ width: 36, height: 36, borderRadius: 8, cursor: "pointer", background: "#F9FAFB", border: "1.5px solid #E5E7EB", display: "flex", alignItems: "center", justifyContent: "center" }}
                  ><Minus size={14} color="#374151" /></button>
                  <div style={{ flex: 1, position: "relative" }}>
                    <input
                      type="number" value={topupCalls} min={TOPUP_MIN} max={TOPUP_MAX} step={TOPUP_STEP}
                      onChange={e => {
                        const n = parseInt(e.target.value, 10);
                        if (!isNaN(n)) setTopupCalls(Math.max(TOPUP_MIN, Math.min(TOPUP_MAX, n)));
                      }}
                      style={{
                        width: "100%", boxSizing: "border-box", height: 36, padding: "0 52px 0 12px",
                        background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 8,
                        fontSize: 14, fontWeight: 600, color: "#111827", outline: "none", textAlign: "center",
                      }}
                      onFocus={e => e.target.style.borderColor = "#7C3AED"}
                      onBlur={e  => {
                        e.target.style.borderColor = "#E5E7EB";
                        setTopupCalls(prev => Math.max(TOPUP_MIN, Math.min(TOPUP_MAX, Math.round(prev / TOPUP_STEP) * TOPUP_STEP)));
                      }}
                    />
                    <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#9CA3AF", pointerEvents: "none" }}>
                      calls
                    </span>
                  </div>
                  <button
                    onClick={() => adjustTopup(+TOPUP_STEP)}
                    style={{ width: 36, height: 36, borderRadius: 8, cursor: "pointer", background: "#F9FAFB", border: "1.5px solid #E5E7EB", display: "flex", alignItems: "center", justifyContent: "center" }}
                  ><Plus size={14} color="#374151" /></button>
                </div>
              </div>
            </>
          )}

          {/* Price summary */}
          <div style={{
            background: isFleetUpgrade ? "#F0FDF4" : isTopup ? "#F5F3FF" : `${selectedPlan?.color || "#4F46E5"}0A`,
            border: `1.5px solid ${isFleetUpgrade ? "#BBF7D0" : isTopup ? "#DDD6FE" : (selectedPlan?.color || "#4F46E5") + "30"}`,
            borderRadius: 10, padding: "12px 16px", marginBottom: 18,
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: isFleetUpgrade ? "#059669" : isTopup ? "#6D28D9" : (selectedPlan?.color || "#4F46E5") }}>
                {isFleetUpgrade ? "Fleet Management" : `${apiCalls.toLocaleString("en-IN")} API calls`}
              </div>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
                ₹{amount.toLocaleString("en-IN")} base + 18% GST
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#111827" }}>
                ₹{grandTotal.toLocaleString("en-IN")}
              </div>
              <div style={{ fontSize: 11, color: "#9CA3AF" }}>total payable</div>
            </div>
          </div>

          {/* PhonePe pay button — hidden on iOS, billing happens on the website instead */}
          {IS_IOS_APP ? (
            <div style={{
              background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10,
              padding: "16px 18px", textAlign: "center",
            }}>
              <Globe size={18} color="#6B7280" style={{ marginBottom: 8 }} />
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginBottom: 4 }}>
                Manage your plan on the web
              </div>
              <div style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.6 }}>
                To keep purchases secure, plans and top-ups are managed at{" "}
                <strong>lorrykhojo.com</strong> from your browser — not inside this app.
              </div>
            </div>
          ) : (
            <>
              <button
                onClick={handlePay}
                disabled={paying}
                style={{
                  width: "100%", height: 46, borderRadius: 10,
                  background: paying ? "#9CA3AF" : "#5f259f",
                  border: "none", color: "#fff", fontSize: 14, fontWeight: 700,
                  cursor: paying ? "default" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  boxShadow: paying ? "none" : "0 2px 12px rgba(95,37,159,0.35)",
                  transition: "background 0.15s",
                  letterSpacing: "-0.1px",
                }}
              >
                {paying ? (
                  <><Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> Redirecting to PhonePe…</>
                ) : (
                  <>
                    {/* PhonePe "Ph" mark */}
                    <span style={{ fontWeight: 900, fontSize: 15, letterSpacing: -1 }}>Ph</span>
                    Pay ₹{grandTotal.toLocaleString("en-IN")} with PhonePe
                  </>
                )}
              </button>

              <div style={{ textAlign: "center", marginTop: 10, fontSize: 11, color: "#9CA3AF" }}>
                Secured by PhonePe · 256-bit SSL · Invoice emailed automatically
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}
